import {Container, ContainerModule, type interfaces} from "inversify";
import {Bindings} from "data/constants/bindings";
import {type IUserStore, UserStore} from "data/stores/user/user.store";
import {
	IModalTutorialController,
	ModalTutorialController,
} from "views/components/modals/modal_tutorial/modal_tutorial.controller";
import {
	ITutorialSliderController,
	TutorialSliderController,
} from "views/components/tutorial_slider/tutorial_slider.controller";
import {AuthController, type IAuthController} from "views/controllers/auth/auth.controller";
import {
	type IMyAccountController,
	MyAccountController,
} from "views/pages/my_account/my_account.controller";
import {API_URL, CONTENT_JSON_URL, JSON_URL, PUB_NUB_ENV, GLOBAL_API} from "data/constants";
import {type IJSONProvider, JSONProvider} from "data/providers/json/json.provider";
import {AuthApiProvider, type IAuthApiProvider} from "data/providers/api/auth.api.provider";
import {HttpClientFactory, type IHttpClientService} from "data/services/http";
import {CountriesStore, type ICountriesStore} from "data/stores/countries/countries.store";
import {ContestsStore, type IContestsStore} from "data/stores/contests/contestsStore";
import {PlayersStore, type IPlayersStore} from "data/stores/players/players.store";
import {SquadsStore, type ISquadsStore} from "data/stores/squads/squads.store";
import {
	type ISecretGateController,
	SecretGateController,
} from "views/controllers/secret_gate/secret_gate.controller";
import {
	type ILocalizationStore,
	LocalizationStore,
} from "data/stores/localization/localization.store";
import {
	type IStaticContentStore,
	StaticContentStore,
} from "data/stores/static_content/static_content.store";
import {HelpController, type IHelpController} from "views/pages/help/help.controller";
import {
	HelpListController,
	type IHelpListController,
} from "views/components/help/help_list/help_list.controller";
import {
	ContactUsController,
	type IContactUsController,
} from "views/components/help/contact_us/contact_us.controller";
import {
	type ILiveScoreController,
	LiveScoreController,
} from "views/controllers/live_score/live_score.controller";
import {ChecksumStore, type IChecksumStore} from "data/stores/checksum/checksum.store";
import {
	type IRegistrationController,
	RegistrationController,
} from "views/pages/registration/registration.controller";
import {type IUserApiProvider, UserApiProvider} from "data/providers/api/user.api.provider";
import {
	type IPasswordApiProvider,
	PasswordApiProvider,
} from "data/providers/api/password.api.provider";
import {
	ForgotPasswordController,
	type IForgotPasswordController,
} from "views/pages/forgot_password/forgot_password.controller";
import {
	type IResetPasswordController,
	ResetPasswordController,
} from "views/pages/reset_password/reset_password.controller";
import {
	type ISessionController,
	SessionController,
} from "views/components/session/session.controller";
import {
	BootstrapController,
	type IBootstrapController,
} from "views/components/bootstrap/bootstrap.controller";
import {type IModalsStore, ModalsStore} from "data/stores/modals/modals.store";
import {
	type IModalErrorController,
	ModalErrorController,
} from "views/components/modals/modal_error/modal_error.controller";
import {
	type IModalConfirmController,
	ModalConfirmController,
} from "views/components/modals/modal_confirm/modal_confirm.controller";
import {
	type ILocalizationController,
	LocalizationController,
} from "views/controllers/localization/localization.controller";
import {IHeaderController, HeaderController} from "views/components/header/header.controller";
import {
	type IPlayersBarController,
	PlayersBarController,
} from "views/components/players/players_bar/players_bar.controller";
import {type IGameController, GameController} from "views/pages/game/game.controller";
import {
	IPlayersBarPlayerController,
	PlayersBarPlayerController,
} from "views/components/players/players_bar/players_bar_player.controller";

import {
	type IContestPlayerStatsStore,
	ContestPlayerStatsStore,
} from "data/stores/contest_player_stats/contest_player_stats.store";

import {
	type IPlayerStatsStore,
	PlayerStatsStore,
} from "data/stores/player_stats/player_stats.store";
import {type IRosterStore, RosterStore} from "data/stores/roster/roster.store";
import {type IRosterProvider, RosterProvider} from "data/providers/api/roster.provider";
import {
	type IBackdoorController,
	BackdoorController,
} from "views/pages/backdoor/backdoor.controller";
import {ITournamentsStore, TournamentsStore} from "data/stores/tournaments/tournaments.store";
import {
	type IMissedPlayersController,
	MissedPlayersController,
} from "views/components/players/missed_players/missed_players.controller";
import {
	ISpinnerController,
	SpinnerController,
} from "views/components/spinner_elements/spinner_framer/spinner.controller";
import {
	IPlayerStatsController,
	PlayerStatsController,
} from "views/controllers/player_stats/player_stats.controller";
import {
	IModalStatsController,
	ModalStatsController,
} from "views/components/modals/modal_stats/modal_stats.controller";
import {type ILandingController, LandingController} from "views/pages/landing/landing.controller";
import {
	type IModalTmpGifController,
	ModalTmpGifController,
} from "views/components/modals/modal_tmp_gif/modal_tmp_gif.controller";
import {
	type IWheelViewController,
	WheelViewController,
} from "views/components/wheel_view/wheel_view.controller";

import {
	IModalPromotedController,
	ModalPromotedController,
} from "views/components/modals/modal_promoted/modal_promoted.controller";
import {GameBarApiProvider, IGameBarApiProvider} from "data/providers/api/gamebar.api.provider";
import {GameBarStore, IGameBarStore} from "data/stores/game_bar/game_bar.store";
import {
	IModalSecondaryRegistrationController,
	ModalSecondaryRegistrationController,
} from "views/components/modals/modal_secondary_registration/modal_secondary_registration.controller";
import {IPromotionStore, PromotionStore} from "data/stores/promotion/promotion.store";
import {
	IModalScoringController,
	ModalScoringController,
} from "views/components/modals/modal_scoring/modal_scoring.controller";
import {
	IRoundStatsController,
	RoundStatsController,
} from "views/components/round_stats/round_stats.controller";

import {
	ContestsNavigationController,
	IContestsNavigationController,
} from "views/components/navigations/contests_navigation/contests_navigation.controller";

import {IRankingsStore, RankingsStore} from "data/stores/rankings/rankings.store";
import {
	ILeaderboardNavigationController,
	LeaderboardNavigationController,
} from "views/components/navigations/leaderboard_navigation.controller";
import {
	ILeaderboardController,
	LeaderboardController,
} from "views/components/leaderboards/leaderboard/leaderboard.controller";
import {IRankingsApiProvider, RankingsApiProvider} from "data/providers/api/rankings.api.provider";
import {IRoundsStore, RoundsStore} from "data/stores/rounds/rounds.store";
import {
	type ICalendarPickerController,
	CalendarPickerController,
} from "views/components/calendar_picker/calendar_picker.controller";
import {
	ModalRosterStatsController,
	IModalRosterStatsController,
} from "views/components/modals/modal_roster_stats/modal_roster_stats.controller";
import {
	type ILeagueLevelsController,
	LeagueLevelsController,
} from "views/pages/league_levels/league_levels.controller";
import {
	type IRoundBadgesController,
	RoundBadgesController,
} from "views/components/round_badges/round_badges.controller";
import {ITutorialStore, TutorialStore} from "data/stores/tutorial/tutorial.store";
import {
	type ILevelContestRosterController,
	LevelContestRosterController,
} from "views/components/roster/level_contest_roster/level_contest_roster.controller";

import {
	type IRoundSummaryStore,
	RoundSummaryStore,
} from "data/stores/round_summary/round_summary.store";
import {
	type ILeaderboardRowController,
	LeaderboardRowController,
} from "views/components/leaderboards/leaderboard_row/leaderboard_row.controller";
import {type IUserRosterStore, UserRosterStore} from "data/stores/user_roster/user_roster.store";
import {
	type ILeaderboardRosterViewController,
	LeaderboardRosterViewController,
} from "views/pages/league_levels/leaderboard_roster_view/leaderboard_roster_view.controller";
import {
	type ILeaderboardProfileNavigationController,
	LeaderboardProfileNavigationController,
} from "views/components/navigations/leaderboard_profile_navigation/leaderboard_profile_navigation.controller";
import {GameBarController, IGameBarController} from "views/components/gamebar/gamebar.controller";
import {IPlayerCalculations, PlayerCalculations} from "data/utils/player.calculations";
import {
	type IHeadToHeadController,
	HeadToHeadController,
} from "views/pages/head_to_head/head_to_head.controller";
import {
	type ITomorrowGameController,
	TomorrowGameController,
} from "views/pages/game/tomorrow_game/tomorrow_game.controller";
import {
	type ITomorrowNavigationController,
	TomorrowNavigationController,
} from "views/components/navigations/tomorrow_navigation/tomorrow_navigation.controller";
import {
	type IModalTomorrowGameController,
	ModalTomorrowGameController,
} from "views/components/modals/modal_tomorrow_game/modal_tomorrow_game.controller";
import {
	type IContestLocksStringController,
	ContestLocksStringController,
} from "views/components/contest_locks_string/contest_locks_string.controller";
import {
	type IH2hNavigationController,
	H2hNavigationController,
} from "views/components/navigations/h2h_navigation/h2h_navigation.controller";
import {
	type IUserLevelBadgeController,
	UserLevelBadgeController,
} from "views/components/user_level_badge/user_level_badge.controller";
import {
	type IFriendsInviteWidgetController,
	FriendsInviteWidgetController,
} from "views/components/friends/friends_invite_widget/friends_invite_widget.controller";
import {
	IH2hManageGamesController,
	H2hManageGamesController,
} from "views/pages/head_to_head/h2h_manage_games/h2h_manage_games.controller";
import {
	type IChallengeGameController,
	ChallengeGameController,
} from "views/components/challenge/challenge_game/challenge_game.controller";
import {
	IRosterListLayoutViewController,
	RosterListLayoutViewController,
} from "views/components/roster/roster_list_layouts/roster_list_layout_view/roster_list_layout_view.controller";
import {
	IRoasterPlayerController,
	RoasterPlayerController,
} from "views/components/roster/roster_player/roster_player.controller";
import {
	IRosterPlayerTournamentInfoController,
	RosterPlayerTournamentInfoController,
} from "views/components/roster/roster_player_tournament_info/roster_player_tournament_info.controller";
import {
	IRosterListController,
	RosterListController,
} from "views/components/roster/roster_list/roster_list.controller";
import {
	IRosterListLayoutViewTomorrowController,
	RosterListLayoutViewTomorrowController,
} from "views/components/roster/roster_list_layouts/roster_list_layout_view_tomorrow/roster_list_layout_view_tomorrow.controller";

import {type IFriendsStore, FriendsStore} from "data/stores/friends/friends.store";
import {FriendsApiProvider, IFriendsApiProvider} from "data/providers/api/friends.api.provider";
import {
	type IH2hFriendsController,
	H2hFriendsController,
} from "views/pages/head_to_head/h2h_friends/h2h_friends.controller";
import {
	type IFriendPlayRowController,
	FriendPlayRowController,
} from "views/components/friends/friend_play_row/friend_play_row.controller";
import {type ISnackbarStore, SnackbarStore} from "data/stores/snackbar/snackbar.store";
import {
	type ISnackbarController,
	SnackbarController,
} from "views/components/snackbar/snackbar.controller";
import {
	PubNubClientService,
	IPubNubClientService,
} from "data/services/pub_nub/pub_nub_client.servise";
import {
	type IPubNubHocController,
	PubNubHocController,
} from "views/components/pub_nub_hoc/pub_nub_hoc.controller";
import {type IH2hProvider, H2hProvider} from "data/providers/h2h/h2h.provider";
import {type IH2hStore, H2hStore} from "data/stores/h2h/h2h.store";
import {
	type IModalH2hChallengeController,
	ModalH2hChallengeController,
} from "views/components/modals/modal_h2h_challenge/modal_h2h_challenge.controller";
import {
	type IChallengeInviteController,
	ChallengeInviteController,
} from "views/components/challenge/challenge_invite/challenge_invite.controller";
import {
	type IH2hChallengeViewController,
	H2hChallengeViewController,
} from "views/pages/head_to_head/h2h_manage_games/h2h_challenge_view/h2h_challenge_view.controller";
import {
	type IH2hRosterMatchUpViewController,
	H2hRosterMatchUpViewController,
} from "views/components/h2h_roster_match_up_view/h2h_roster_match_up_view.controller";
import {
	type IHocFriendsRedirectController,
	HocFriendsRedirectController,
} from "views/components/hoc_friends_redirect/hoc_friends_redirect.controller";
import {
	ILeaderboardsLayoutController,
	LeaderboardsLayoutController,
} from "views/components/leaderboards/leaderboards_layout/leaderboards_layout.controller";
import {
	IContextualPromptBannerController,
	ContextualPromptBannerController,
} from "views/components/contextual_prompt_banner/contextual_prompt_banner.controller";
import {
	IContextualPromptBannerStore,
	ContextualPromptBannerStore,
} from "data/stores/contextual_prompt_banner/contextual_prompt_banner.store";
import {
	IContextualPromptBannerProvider,
	ContextualPromptBannerProvider,
} from "data/providers/api/contextual_prompt_banner/contextual_prompt_banner.provider";
import {
	ModalAcceptTermsRegistrationController,
	IModalAcceptTermsRegistrationController,
} from "views/components/modals/modal_accept_terms/modal_accept_terms.controller";

export const DIContainer = new Container();

export const services = new ContainerModule((bind: interfaces.Bind) => {
	bind<IHttpClientService>(Bindings.ApiHTTPClient).toConstantValue(
		HttpClientFactory.createApiClient({
			baseURL: API_URL,
			withCredentials: true,
		})
	);

	bind<IHttpClientService>(Bindings.JsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: JSON_URL,
		})
	);

	bind<IHttpClientService>(Bindings.ContentJsonHTTPClient).toConstantValue(
		HttpClientFactory.createJSONClient({
			baseURL: CONTENT_JSON_URL,
		})
	);

	bind<IPubNubClientService>(Bindings.PubNubClientService).toConstantValue(
		new PubNubClientService(
			{
				subscribeKey: "sub-c-62ed57e3-9b9a-42bc-8c21-13de9be5759a",
			},
			PUB_NUB_ENV
		)
	);

	bind<IHttpClientService>(Bindings.GlobalApiHTTPClient).toConstantValue(
		HttpClientFactory.createApiClient({
			baseURL: GLOBAL_API,
			withCredentials: true,
		})
	);
});

export const providers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IJSONProvider>(Bindings.JSONProvider).to(JSONProvider);
	bind<IAuthApiProvider>(Bindings.AuthApiProvider).to(AuthApiProvider);
	bind<IUserApiProvider>(Bindings.UserApiProvider).to(UserApiProvider);
	bind<IPasswordApiProvider>(Bindings.PasswordApiProvider).to(PasswordApiProvider);
	bind<IRosterProvider>(Bindings.RosterProvider).to(RosterProvider);
	bind<IGameBarApiProvider>(Bindings.GameBarApiProvider).to(GameBarApiProvider);
	bind<IRankingsApiProvider>(Bindings.RankingsApiProvider).to(RankingsApiProvider);
	bind<IFriendsApiProvider>(Bindings.FriendsApiProvider).to(FriendsApiProvider);
	bind<IH2hProvider>(Bindings.H2hProvider).to(H2hProvider);
	bind<IContextualPromptBannerProvider>(Bindings.ContextualPromptBannerProvider).to(
		ContextualPromptBannerProvider
	);
});

export const stores = new ContainerModule((bind: interfaces.Bind) => {
	bind<ILocalizationStore>(Bindings.LocalizationStore).to(LocalizationStore).inSingletonScope();
	bind<IUserStore>(Bindings.UserStore).to(UserStore).inSingletonScope();
	bind<IContestsStore>(Bindings.ContestsStore).to(ContestsStore).inSingletonScope();
	bind<IPlayersStore>(Bindings.PlayersStore).to(PlayersStore).inSingletonScope();
	bind<ISquadsStore>(Bindings.SquadsStore).to(SquadsStore).inSingletonScope();

	bind<ICountriesStore>(Bindings.CountriesStore).to(CountriesStore).inSingletonScope();
	bind<IStaticContentStore>(Bindings.StaticContentStore)
		.to(StaticContentStore)
		.inSingletonScope();

	bind<IChecksumStore>(Bindings.ChecksumStore).to(ChecksumStore).inSingletonScope();
	bind<IModalsStore>(Bindings.ModalsStore).to(ModalsStore).inSingletonScope();
	bind<IContestPlayerStatsStore>(Bindings.ContestPlayerStatsStore)
		.to(ContestPlayerStatsStore)
		.inSingletonScope();
	bind<IPlayerStatsStore>(Bindings.PlayerStatsStore).to(PlayerStatsStore).inSingletonScope();
	bind<IRosterStore>(Bindings.RosterStore).to(RosterStore).inSingletonScope();
	bind<ITournamentsStore>(Bindings.TournamentsStore).to(TournamentsStore).inSingletonScope();
	bind<IGameBarStore>(Bindings.GameBarStore).to(GameBarStore).inSingletonScope();
	bind<IPromotionStore>(Bindings.PromotionStore).to(PromotionStore).inSingletonScope();
	bind<IRankingsStore>(Bindings.RankingsStore).to(RankingsStore).inSingletonScope();
	bind<IRoundsStore>(Bindings.RoundsStore).to(RoundsStore).inSingletonScope();
	bind<ITutorialStore>(Bindings.TutorialStore).to(TutorialStore).inSingletonScope();
	bind<IRoundSummaryStore>(Bindings.RoundSummaryStore).to(RoundSummaryStore).inSingletonScope();
	bind<IUserRosterStore>(Bindings.UserRosterStore).to(UserRosterStore).inSingletonScope();
	bind<IPlayerCalculations>(Bindings.PlayerCalculations)
		.to(PlayerCalculations)
		.inSingletonScope();
	bind<IFriendsStore>(Bindings.FriendsStore).to(FriendsStore).inSingletonScope();
	bind<ISnackbarStore>(Bindings.SnackbarStore).to(SnackbarStore).inSingletonScope();
	bind<IH2hStore>(Bindings.H2hStore).to(H2hStore).inSingletonScope();
	bind<IContextualPromptBannerStore>(Bindings.ContextualPromptBannerStore)
		.to(ContextualPromptBannerStore)
		.inSingletonScope();
});

export const controllers = new ContainerModule((bind: interfaces.Bind) => {
	bind<IAuthController>(Bindings.AuthController).to(AuthController);
	bind<IForgotPasswordController>(Bindings.ForgotPasswordController).to(ForgotPasswordController);
	bind<IResetPasswordController>(Bindings.ResetPasswordController).to(ResetPasswordController);
	bind<IRegistrationController>(Bindings.RegistrationController).to(RegistrationController);
	bind<IMyAccountController>(Bindings.MyAccountController).to(MyAccountController);
	bind<ISecretGateController>(Bindings.SecretGateController).to(SecretGateController);
	bind<IHelpController>(Bindings.HelpController).to(HelpController);
	bind<IHelpListController>(Bindings.HelpListController).to(HelpListController);
	bind<IContactUsController>(Bindings.ContactUsController).to(ContactUsController);
	bind<ILiveScoreController>(Bindings.LiveScoreController).to(LiveScoreController);
	bind<ISessionController>(Bindings.SessionController).to(SessionController);
	bind<IBootstrapController>(Bindings.BootstrapController).to(BootstrapController);
	bind<IModalErrorController>(Bindings.ModalErrorController).to(ModalErrorController);
	bind<IModalConfirmController>(Bindings.ModalConfirmController).to(ModalConfirmController);
	bind<ILocalizationController>(Bindings.LocalizationController).to(LocalizationController);
	bind<IHeaderController>(Bindings.HeaderController).to(HeaderController);
	bind<IPlayersBarController>(Bindings.PlayersBarController).to(PlayersBarController);
	bind<IGameController>(Bindings.GameController).to(GameController);
	bind<IPlayersBarPlayerController>(Bindings.PlayersBarPlayerController).to(
		PlayersBarPlayerController
	);
	bind<IPlayerStatsController>(Bindings.PlayerStatsController).to(PlayerStatsController);
	bind<IBackdoorController>(Bindings.BackdoorController).to(BackdoorController);
	bind<ILeaderboardsLayoutController>(Bindings.LeaderboardsLayoutController).to(
		LeaderboardsLayoutController
	);
	bind<ILeaderboardController>(Bindings.LeaderboardController).to(LeaderboardController);
	bind<IMissedPlayersController>(Bindings.MissedPlayersController).to(MissedPlayersController);
	bind<ISpinnerController>(Bindings.SpinnerController).to(SpinnerController);
	bind<IModalStatsController>(Bindings.ModalStatsController).to(ModalStatsController);
	bind<IModalRosterStatsController>(Bindings.ModalRosterStatsController).to(
		ModalRosterStatsController
	);
	bind<ILandingController>(Bindings.LandingController).to(LandingController);
	bind<IModalTmpGifController>(Bindings.ModalTmpGifController).to(ModalTmpGifController);
	bind<IWheelViewController>(Bindings.WheelViewController).to(WheelViewController);
	bind<IRosterListLayoutViewController>(Bindings.RosterListViewController).to(
		RosterListLayoutViewController
	);
	bind<IModalPromotedController>(Bindings.ModalPromotedController).to(ModalPromotedController);
	bind<IModalSecondaryRegistrationController>(Bindings.ModalSecondaryRegistrationController).to(
		ModalSecondaryRegistrationController
	);
	bind<IModalScoringController>(Bindings.ModalScoringController).to(ModalScoringController);
	bind<IRoundStatsController>(Bindings.RoundStatsController).to(RoundStatsController);
	bind<IRoasterPlayerController>(Bindings.RoasterPlayerController).to(RoasterPlayerController);
	bind<IContestsNavigationController>(Bindings.ContestsNavigationController).to(
		ContestsNavigationController
	);
	bind<IRosterPlayerTournamentInfoController>(Bindings.RosterPlayerTournamentInfoController).to(
		RosterPlayerTournamentInfoController
	);
	bind<ILeaderboardNavigationController>(Bindings.LeaderboardNavigationController).to(
		LeaderboardNavigationController
	);
	bind<ICalendarPickerController>(Bindings.CalendarPickerController).to(CalendarPickerController);
	bind<ILeagueLevelsController>(Bindings.LeagueLevelsController).to(LeagueLevelsController);
	bind<IRoundBadgesController>(Bindings.RoundBadgesController).to(RoundBadgesController);
	bind<IModalTutorialController>(Bindings.ModalTutorialController).to(ModalTutorialController);
	bind<ITutorialSliderController>(Bindings.TutorialSliderController).to(TutorialSliderController);
	bind<ILevelContestRosterController>(Bindings.LevelContestRosterController).to(
		LevelContestRosterController
	);
	bind<IRosterListController>(Bindings.RosterLevelViewController).to(RosterListController);
	bind<ILeaderboardRowController>(Bindings.LeaderboardRowController).to(LeaderboardRowController);
	bind<ILeaderboardRosterViewController>(Bindings.LeaderboardRosterViewController).to(
		LeaderboardRosterViewController
	);
	bind<ILeaderboardProfileNavigationController>(
		Bindings.LeaderboardProfileNavigationController
	).to(LeaderboardProfileNavigationController);
	bind<IGameBarController>(Bindings.GameBarController).to(GameBarController);
	bind<IHeadToHeadController>(Bindings.HeadToHeadController).to(HeadToHeadController);
	bind<ITomorrowGameController>(Bindings.TomorrowGameController).to(TomorrowGameController);
	bind<ITomorrowNavigationController>(Bindings.TomorrowNavigationController).to(
		TomorrowNavigationController
	);
	bind<IRosterListLayoutViewTomorrowController>(
		Bindings.RosterListLayoutViewTomorrowController
	).to(RosterListLayoutViewTomorrowController);
	bind<IModalTomorrowGameController>(Bindings.ModalTomorrowGameController).to(
		ModalTomorrowGameController
	);
	bind<IContestLocksStringController>(Bindings.ContestLocksStringController).to(
		ContestLocksStringController
	);
	bind<IH2hNavigationController>(Bindings.H2hNavigationController).to(H2hNavigationController);
	bind<IUserLevelBadgeController>(Bindings.UserLevelBadgeController).to(UserLevelBadgeController);
	bind<IFriendsInviteWidgetController>(Bindings.FriendsInviteWidgetController).to(
		FriendsInviteWidgetController
	);
	bind<IH2hManageGamesController>(Bindings.H2hManageGamesController).to(H2hManageGamesController);
	bind<IChallengeGameController>(Bindings.ChallengeGameController).to(ChallengeGameController);
	bind<IH2hFriendsController>(Bindings.H2hFriendsController).to(H2hFriendsController);
	bind<IFriendPlayRowController>(Bindings.FriendPlayRowController).to(FriendPlayRowController);
	bind<ISnackbarController>(Bindings.SnackbarController).to(SnackbarController);
	bind<IPubNubHocController>(Bindings.PubNubHocController).to(PubNubHocController);
	bind<IModalH2hChallengeController>(Bindings.ModalH2hChallengeController).to(
		ModalH2hChallengeController
	);
	bind<IChallengeInviteController>(Bindings.ChallengeInviteController).to(
		ChallengeInviteController
	);
	bind<IH2hChallengeViewController>(Bindings.H2hChallengeViewController).to(
		H2hChallengeViewController
	);
	bind<IH2hRosterMatchUpViewController>(Bindings.H2hRosterMatchUpViewController).to(
		H2hRosterMatchUpViewController
	);
	bind<IHocFriendsRedirectController>(Bindings.HocFriendsRedirectController).to(
		HocFriendsRedirectController
	);
	bind<IContextualPromptBannerController>(Bindings.ContextualPromptBannerController).to(
		ContextualPromptBannerController
	);

	bind<IModalAcceptTermsRegistrationController>(
		Bindings.ModalAcceptTermsRegistrationController
	).to(ModalAcceptTermsRegistrationController);
});
